<script setup lang="ts">
defineProps<{ data: NumberedListProps; chapterIndex?: number; template?: 'page' | 'overlay' }>()
const linkComponent = resolveComponent('NuxtLink')
const itemRefs = useTemplateRef('items')
const borderRefs = useTemplateRef('borders')
const list = ref<HTMLElement | null>(null)

// ------- GSAP ANIMATION -------
const { $gsap, $ScrollTrigger } = useNuxtApp()

async function cleanupAnimation(): Promise<void> {
  $ScrollTrigger.getById('listReveal')?.kill()

  if (itemRefs.value?.length > 0) {
    $gsap.set(itemRefs.value, { clearProps: 'all' })
  }

  await Promise.resolve()
}

async function initAnimation() {
  if (itemRefs.value?.length <= 0) return
  const tl = $gsap.timeline({
    scrollTrigger: {
      id: 'listReveal',
      trigger: list.value,
      // markers: true,
      start: `top 80%`,
      end: `bottom top`,
      toggleActions: 'play reverse play reverse',
    },
  })
  tl.fromTo(
    itemRefs.value,
    {
      yPercent: -120,
    },
    {
      yPercent: 0,
      ease: 'power2.inOut',
      stagger: 0.1,
    },
  ).fromTo(
    borderRefs.value,
    {
      scaleX: 0,
    },
    {
      scaleX: 1,
      transformOrigin: 'center center',
      ease: 'power2.inOut',
      stagger: 0.1,
    },
    '-=.5',
  )
}

onMounted(async () => {
  await nextTick()
  await cleanupAnimation()
  await initAnimation()
})

onBeforeUnmount(async () => {
  await cleanupAnimation()
})
</script>

<template>
  <section
    class="px-bleed"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
      'lg:py-40': data.trumpet || data.title,
      'pt-5': !(data.trumpet || data.title),
    }"
  >
    <div
      class="flex flex-col items-center text-center"
      :class="{ 'pb-25 max-lg:pt-25': data.trumpet || data.title }"
    >
      <ElementsChapter
        v-if="data.trumpet && chapterIndex"
        :chapter="data.trumpet"
        :index="chapterIndex"
      />

      <ElementsTitleRevealv2
        :title="data.title"
        class="font-display mt-6 max-w-[13em] text-3xl uppercase font-stretch-expanded lg:mt-7 lg:text-5xl"
      />
    </div>

    <ol ref="list">
      <li
        v-for="(item, idx) in data.items"
        :key="idx"
        class="relative"
        :class="{
          'transition-colors duration-300 before:pointer-events-none before:absolute before:-inset-0 before:z-0 before:scale-x-[0] before:bg-black before:transition-all before:duration-500 before:ease-in-out after:pointer-events-none after:absolute after:inset-0 after:z-1 hover:text-white hover:before:scale-x-[1.1] hover:before:-skew-x-[20deg]':
            item.callToAction?.[0]?.url,
        }"
      >
        <div class="overflow-clip">
          <div
            ref="items"
            class="item-wrapper gap-gap group relative grid grid-cols-6 py-6 lg:grid-cols-12 lg:py-7"
          >
            <div
              ref="borders"
              class="line-border absolute top-0 left-0 h-[1px] w-full origin-center bg-black/50"
            />
            <span
              class="font-display relative z-1 col-span-2 text-2xl uppercase font-stretch-expanded transition-colors duration-300 lg:col-span-6 lg:text-3xl"
            >
              {{ idx + 1 }}
            </span>

            <component
              :is="item.callToAction?.[0]?.url ? linkComponent : 'span'"
              :href="item.callToAction?.[0]?.url"
              :target="item.callToAction?.[0]?.target"
              class="col-span-4 lg:col-span-6"
              :class="{ 'after:absolute after:inset-0 after:z-1': item.callToAction?.[0]?.url }"
            >
              <div class="relative z-1">
                <h3
                  class="font-display text-xl uppercase font-stretch-expanded"
                  :class="{ underline: item.callToAction?.[0]?.url }"
                >
                  {{ item.title }}
                </h3>

                <ElementsPortableTextWrapper class="pt-4 lg:text-lg" :content="item.text" />
              </div>
            </component>
          </div>
        </div>
      </li>
    </ol>
  </section>
</template>
